import { useId, type ReactNode } from "react";
import { cn } from "cn";

type Option<T extends string> = {
  value: T;
  /** Words, or words with a count beside them. The radio is named by whatever this reads as. */
  label: ReactNode;
  tone?: "default" | "success" | "danger";
};

/**
 * Always / Ask first / Never. One radio group per row; the label names the row for screen readers.
 *
 * Native radios rather than buttons: arrow keys, the single tab stop and form semantics are the
 * browser's, so none of them can drift. The inputs are visually hidden and the label is the
 * target, which makes the whole pill clickable and lets the focus ring sit on the pill through
 * `:has(:focus-visible)`. `tone` colors only the selected option's text, because a green "Always"
 * or red "Never" at rest would read as a verdict on an option nobody has chosen.
 *
 * It is controlled and has no uncontrolled mode: the value is a decision somebody else records.
 */
export function Segmented<T extends string>({
  label,
  value,
  options,
  onValueChange,
  disabled,
  size = "sm",
}: {
  label: string;
  value: T;
  options: readonly Option<T>[];
  onValueChange: (value: T) => void;
  disabled?: boolean | undefined;
  size?: "sm" | "md";
}) {
  const name = useId();
  return (
    <div
      role="radiogroup"
      aria-label={label}
      data-slot="segmented"
      className={cn("inline-flex shrink-0 rounded-lg bg-muted p-0.5", disabled && "opacity-60")}
    >
      {options.map((option) => {
        const selected = option.value === value;
        return (
          <label
            key={option.value}
            className={cn(
              "relative cursor-pointer rounded-md text-center whitespace-nowrap select-none",
              "transition-[background-color,color,box-shadow] duration-fast ease-out motion-reduce:transition-none",
              "has-focus-visible:ring-3 has-focus-visible:ring-(--focus-halo)",
              size === "sm" ? "px-2.5 py-1 text-xs" : "px-3 py-1.5 text-(length:--text-ui)",
              selected
                ? cn(
                    "bg-card font-medium shadow-sm",
                    option.tone === "success" && "text-success",
                    option.tone === "danger" && "text-destructive",
                  )
                : "text-muted-foreground hover:text-foreground",
              disabled && "cursor-not-allowed hover:text-muted-foreground",
            )}
          >
            <input
              type="radio"
              className="sr-only"
              name={name}
              value={option.value}
              checked={selected}
              disabled={disabled}
              onChange={() => onValueChange(option.value)}
            />
            {option.label}
          </label>
        );
      })}
    </div>
  );
}
